import { FC, useState } from 'react';
import { Box, Button, Chip, Collapse, Divider, Fade, Grid, IconButton, Stack, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import DirectionsRunIcon from '@mui/icons-material/DirectionsRun';
import SchoolIcon from '@mui/icons-material/School';
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety';
import RedeemIcon from '@mui/icons-material/Redeem';
import PlaceIcon from '@mui/icons-material/Place';
import PhoneIcon from '@mui/icons-material/Phone';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import useTranslations from '../global/translation/hooks/translation';
import { useThemeContext } from '../global/ThemeContext';
import promykSaleImg from '../../../app/media/promyk_sale.jpg';
import { designTokens } from '../../theme/designTokens';

const Horses: FC = () => {
  const { currentLanguage, translations } = useTranslations();
  const theme = useTheme();
  const { darkMode } = useThemeContext();
  const palette = darkMode ? designTokens.colors.dark : designTokens.colors.light;
  const [expanded, setExpanded] = useState(false);
  const [currentHorseIndex, setCurrentHorseIndex] = useState(0);

  const horses = [
    {
      id: 'promyk',
      image: promykSaleImg,
      discipline: 'horseDisciplineTag',
      title: 'horseSectionSubtitle',
      description: '2018 | 8-letni kasztanowaty wałach SP, elastyczny z zapasem mocy, starty do L (1.00 m), w domu skacze wyżej.',
      price: 'horsePriceValue',
      priceLabel: 'horsePriceLabel',
      showDetails: true,
    },
    {
      id: 'comingSoon',
      image: null,
      discipline: 'horseDisciplineTag',
      title: 'horseSectionTitle',
      description: 'horseComingSoon',
      price: null,
      priceLabel: null,
      showDetails: false,
    },
  ];

  const currentHorse = horses[currentHorseIndex];

  const handleNextHorse = () => {
    setCurrentHorseIndex((prev) => (prev + 1) % horses.length);
    setExpanded(false);
  };

  const handlePrevHorse = () => {
    setCurrentHorseIndex((prev) => (prev - 1 + horses.length) % horses.length);
    setExpanded(false);
  };

  const handleExpandClick = () => {
    setExpanded((prev) => !prev);
  };

  const featureItems = [
    { key: 'horseSaleProfile', icon: <EmojiEventsIcon fontSize="small" /> },
    { key: 'horseSaleTemperament', icon: <DirectionsRunIcon fontSize="small" /> },
    { key: 'horseSaleTraining', icon: <SchoolIcon fontSize="small" /> },
    { key: 'horseSaleHealth', icon: <HealthAndSafetyIcon fontSize="small" /> },
    { key: 'horseSaleExtras', icon: <RedeemIcon fontSize="small" /> },
    { key: 'horseSaleLocation', icon: <PlaceIcon fontSize="small" /> },
  ] as const;

  const borderTone = darkMode ? palette.border.default : 'rgba(20, 56, 125,0.12)';

  return (
    <Grid
      my={6}
      py={{ xs: 6, md: 10 }}
      container
      justifyContent="center"
      alignItems="center"
      sx={{ bgcolor: darkMode ? palette.background.default : '#FFFFFF', maxWidth: '1200px', mx: 'auto', px: { xs: 2, md: 4 }, borderRadius: { xs: 0, md: '16px' } }}
    >
      <Grid item xs={12} mb={4} textAlign="center">
        <Typography variant="h4" fontWeight={300} sx={{ color: darkMode ? '#FFFFFF' : palette.primary, letterSpacing: '-0.5px' }}>
          {translations.label.horseSectionTitle[currentLanguage]}
        </Typography>
      </Grid>

      <Grid item xs={12} md={10}>
        <Fade in timeout={600} key={currentHorseIndex}>
          <Box
            sx={{
              position: 'relative',
              overflow: 'hidden',
              borderRadius: '16px',
              border: `1px solid ${borderTone}`,
              bgcolor: darkMode ? palette.background.elevated : '#FFFFFF',
              color: darkMode ? '#FFFFFF' : palette.text.primary,
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': {
                boxShadow: darkMode ? '0 12px 24px rgba(0, 0, 0, 0.32)' : '0 18px 38px rgba(20, 56, 125, 0.12)',
              },
            }}
          >
            {currentHorse.image ? (
              <Box sx={{ position: 'relative', paddingTop: '56.25%' }}>
                <Box
                  component="img"
                  src={currentHorse.image}
                  alt={`Horse ${currentHorseIndex}`}
                  sx={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'cover' }}
                />
              </Box>
            ) : (
              <Box
                sx={{
                  width: '100%',
                  minHeight: { xs: 280, md: 420 },
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  px: 3,
                  textAlign: 'center',
                  background: darkMode
                    ? 'linear-gradient(145deg, #102C5B 0%, #07142B 100%)'
                    : 'linear-gradient(145deg, #EEF4FD 0%, #FFFFFF 100%)',
                }}
              >
                <Typography variant="h5" fontWeight={500} sx={{ color: darkMode ? palette.text.primary : palette.primary }}>
                  {translations.label.horseComingSoon[currentLanguage]}
                </Typography>
              </Box>
            )}

            {currentHorse.image && (
            <Box sx={{ p: { xs: 2, sm: 3, md: 4 }, position: 'relative' }}>
              <Stack direction="row" spacing={1.5} mb={2} flexWrap="wrap" sx={{ gap: 1 }}>
                <Chip
                  label={translations.label[currentHorse.discipline as keyof typeof translations.label][currentLanguage]}
                  size="small"
                  sx={{
                    bgcolor: 'transparent',
                    color: theme.palette.primary.main,
                    border: `1px solid ${theme.palette.primary.main}`,
                    fontWeight: 500,
                  }}
                />
              </Stack>

              <Typography
                variant="h5"
                fontWeight={400}
                mb={3}
                sx={{
                  color: darkMode ? '#FFFFFF' : '#000000',
                  fontSize: { xs: '1.25rem', sm: '1.5rem' },
                  wordBreak: 'break-word',
                }}
              >
                {translations.label[currentHorse.title as keyof typeof translations.label][currentLanguage]}
              </Typography>

              {currentHorse.price && (
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3} alignItems={{ xs: 'stretch', sm: 'flex-end' }} mb={3}>
                  <Box sx={{ flex: { xs: 'none', sm: 1 } }}>
                    <Typography variant="caption" fontWeight={600} sx={{ color: theme.palette.primary.main }}>
                      {translations.label[currentHorse.priceLabel as keyof typeof translations.label][currentLanguage]}
                    </Typography>
                    <Typography
                      variant="h4"
                      fontWeight={300}
                      sx={{
                        color: darkMode ? '#FFFFFF' : '#000000',
                        fontSize: { xs: '1.75rem', sm: '2.125rem' },
                      }}
                    >
                      {translations.label[currentHorse.price as keyof typeof translations.label][currentLanguage]}
                    </Typography>
                  </Box>
                  <Stack direction="row" spacing={1.5} sx={{ flex: { xs: 'none', sm: 1 } }}>
                    <Button
                      variant="contained"
                      size="medium"
                      fullWidth
                      startIcon={<PhoneIcon />}
                      onClick={() => window.open('tel:729883891', '_self')}
                      sx={{
                        bgcolor: theme.palette.primary.main,
                        color: '#FFFFFF',
                        fontWeight: 600,
                        textTransform: 'none',
                        boxShadow: 'none',
                        '&:hover': {
                          bgcolor: theme.palette.primary.dark,
                          transform: 'translateY(-2px)',
                          boxShadow: '0 8px 16px rgba(0, 0, 0, 0.15)',
                        },
                      }}
                    >
                      {translations.label.horseCTACall[currentLanguage]}
                    </Button>
                  </Stack>
                </Stack>
              )}

              {currentHorse.showDetails && (
                <>
                  <Collapse in={expanded} timeout="auto" unmountOnExit>
                    <Divider sx={{ my: 3, borderColor: borderTone }} />
                    <Grid container spacing={3} mb={3}>
                      <Grid item xs={12} sm={6}>
                        <Stack spacing={1.5}>
                          {featureItems.map(({ key, icon }) => (
                            <Stack key={key} direction="row" spacing={1.5} alignItems="flex-start">
                              <Box
                                sx={{
                                  width: 24,
                                  height: 24,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  color: theme.palette.primary.main,
                                }}
                              >
                                {icon}
                              </Box>
                              <Typography variant="body1" sx={{ color: darkMode ? '#CCCCCC' : '#555555' }}>
                                {translations.label[key as keyof typeof translations.label][currentLanguage]}
                              </Typography>
                            </Stack>
                          ))}
                        </Stack>
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <Stack spacing={2}>
                          <Grid container spacing={1}>
                            {[
                              {
                                label: translations.label.horseSex[currentLanguage],
                                value: translations.label.horseSexGelding[currentLanguage],
                              },
                              {
                                label: translations.label.horseBirthDate[currentLanguage],
                                value: translations.label.horseBirthYear[currentLanguage],
                              },
                              {
                                label: translations.label.horsePurpose[currentLanguage],
                                value: translations.label.horseJumping[currentLanguage],
                              },
                              {
                                label: translations.label.horseCoat[currentLanguage],
                                value: translations.label.horseChestnut[currentLanguage],
                              },
                              {
                                label: translations.label.horseBreed[currentLanguage],
                                value: translations.label.horseBreedType[currentLanguage],
                              },
                            ].map((item, idx) => (
                              <Grid key={idx} item xs={12}>
                                <Stack direction="row" spacing={1}>
                                  <Typography variant="body2" fontWeight={600} sx={{ color: theme.palette.primary.main, minWidth: '110px' }}>
                                    {item.label}:
                                  </Typography>
                                  <Typography variant="body2" sx={{ color: darkMode ? '#CCCCCC' : '#555555' }}>
                                    {item.value}
                                  </Typography>
                                </Stack>
                              </Grid>
                            ))}
                          </Grid>

                          <Box>
                            <Typography
                              variant="caption"
                              fontWeight={600}
                              sx={{ color: theme.palette.primary.main, textTransform: 'none', letterSpacing: 0.3 }}
                            >
                              {translations.label.horseCharacterTitle[currentLanguage]}
                            </Typography>
                            <Stack direction="row" spacing={1} flexWrap="wrap" mt={1} sx={{ gap: 1 }}>
                              {[
                                translations.label.horseProfessor[currentLanguage],
                                translations.label.horseBeginners[currentLanguage],
                                translations.label.horseExperienced[currentLanguage],
                                translations.label.horseTerrain[currentLanguage],
                                translations.label.horseDogFriendly[currentLanguage],
                              ].map((tag, idx) => (
                                <Chip
                                  key={idx}
                                  label={tag}
                                  size="small"
                                  variant="outlined"
                                  sx={{ borderColor: borderTone, color: darkMode ? '#CCCCCC' : '#555555', fontSize: '0.75rem' }}
                                />
                              ))}
                            </Stack>
                          </Box>

                          <Box>
                            <Typography
                              variant="caption"
                              fontWeight={600}
                              sx={{ color: theme.palette.primary.main, textTransform: 'none', letterSpacing: 0.3 }}
                            >
                              {translations.label.horseCompetitionTitle[currentLanguage]}
                            </Typography>
                            <Stack direction="row" spacing={1} flexWrap="wrap" mt={1} sx={{ gap: 1 }}>
                              {[translations.label.horseSocial[currentLanguage], translations.label.horseRegional[currentLanguage]].map((tag, idx) => (
                                <Chip
                                  key={idx}
                                  label={tag}
                                  size="small"
                                  variant="outlined"
                                  sx={{ borderColor: borderTone, color: darkMode ? '#CCCCCC' : '#555555', fontSize: '0.75rem' }}
                                />
                              ))}
                            </Stack>
                          </Box>

                          <Box>
                            <Typography
                              variant="caption"
                              fontWeight={600}
                              sx={{ color: theme.palette.primary.main, textTransform: 'none', letterSpacing: 0.3 }}
                            >
                              {translations.label.horseFeaturesTitle[currentLanguage]}
                            </Typography>
                            <Stack direction="row" spacing={1} flexWrap="wrap" mt={1} sx={{ gap: 1 }}>
                              {[
                                translations.label.horseTransport[currentLanguage],
                                translations.label.horseFarrier[currentLanguage],
                                translations.label.horseHooves[currentLanguage],
                                translations.label.horseGroundwork[currentLanguage],
                                translations.label.horseWithEquipment[currentLanguage],
                                translations.label.horsePhysiotherapy[currentLanguage],
                              ].map((tag, idx) => (
                                <Chip
                                  key={idx}
                                  label={tag}
                                  size="small"
                                  variant="outlined"
                                  sx={{ borderColor: borderTone, color: darkMode ? '#CCCCCC' : '#555555', fontSize: '0.75rem' }}
                                />
                              ))}
                            </Stack>
                          </Box>
                        </Stack>
                      </Grid>
                    </Grid>
                  </Collapse>

                  <Button
                    onClick={handleExpandClick}
                    size="small"
                    sx={{
                      color: theme.palette.primary.main,
                      textTransform: 'none',
                      fontWeight: 600,
                      mt: 2,
                      '&:hover': { backgroundColor: darkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)' },
                    }}
                  >
                    {expanded ? translations.typography.readLess[currentLanguage] : translations.typography.readMore[currentLanguage]}
                  </Button>
                </>
              )}
            </Box>
            )}
          </Box>
        </Fade>

        {/* Navigation Arrows */}
        <Stack direction="row" justifyContent="space-between" alignItems="center" mt={2}>
          <IconButton
            onClick={handlePrevHorse}
            aria-label={currentLanguage === 'pl' ? 'Poprzedni koń' : 'Previous horse'}
            sx={{
              color: theme.palette.primary.main,
              '&:hover': {
                backgroundColor: darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.04)',
              },
            }}
          >
            <NavigateBeforeIcon />
          </IconButton>
          <Stack direction="row" spacing={1} justifyContent="center" flex={1}>
            {horses.map((_, idx) => (
              <Box
                component="button"
                key={idx}
                type="button"
                aria-label={currentLanguage === 'pl' ? `Pokaż konia ${idx + 1}` : `Show horse ${idx + 1}`}
                aria-current={idx === currentHorseIndex ? 'true' : undefined}
                onClick={() => {
                  setCurrentHorseIndex(idx);
                  setExpanded(false);
                }}
                sx={{
                  width: 12,
                  height: 12,
                  p: 0,
                  border: 0,
                  borderRadius: '50%',
                  backgroundColor: idx === currentHorseIndex ? theme.palette.primary.main : borderTone,
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    backgroundColor: idx === currentHorseIndex ? theme.palette.primary.main : darkMode ? 'rgba(255,255,255,0.2)' : 'rgba(16,35,90,0.12)',
                  },
                }}
              />
            ))}
          </Stack>
          <IconButton
            onClick={handleNextHorse}
            aria-label={currentLanguage === 'pl' ? 'Następny koń' : 'Next horse'}
            sx={{
              color: theme.palette.primary.main,
              '&:hover': {
                backgroundColor: darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.04)',
              },
            }}
          >
            <NavigateNextIcon />
          </IconButton>
        </Stack>
      </Grid>
    </Grid>
  );
};

export default Horses;
